import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';
import { SUBSCRIPTION_PLANS } from '../services/MonetizationService';
import MonetizationService from '../services/MonetizationService';
import StripeService from '../services/StripeService';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';
import DisModal from '../components/design-system/DisModal';
import DisInput from '../components/design-system/DisInput';
import StripePaymentForm from '../components/StripePaymentForm';
import Footer from '../components/Footer';
import FloatingMenu from '../components/FloatingMenu';
import StarConversionSection from '../components/StarConversionSection';
import { trackEvent, EVENTS } from '../services/analytics';

const SubscriptionPage = ({ onNavigateBack, onNavigateToHistory, onNavigateToStory, onNavigateToAdminPanel, onNavigateToChat }) => {
  const { user, api, logout } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [myReferralCode, setMyReferralCode] = useState('');
  const [planChangeData, setPlanChangeData] = useState(null);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [scheduledChange, setScheduledChange] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [pendingDowngrade, setPendingDowngrade] = useState(null);
  // États pour les toggles mensuel/annuel de chaque plan card
  const [essentialIsYearly, setEssentialIsYearly] = useState(false);
  const [familyIsYearly, setFamilyIsYearly] = useState(false);
  
  const {
    subscription,
    currentPlan,
    usage,
    trialDaysLeft,
    questionsLeft,
    questionsUsedToday,
    childrenLeft,
    storiesThisMonth,
    maxStories,
    storiesRemaining,
    isPremium,
    isFamily,
    formatPrice,
    subscribeToPlan,
    cancelSubscription,
    refreshData,
  } = useMonetization();

  const stripeService = StripeService.getInstance();

  const monetizationService = new MonetizationService();

  // Déterminer si l'utilisateur est admin (via user.is_admin OU plan_id contenant 'admin' OU incohérence trial+premium)
  const isAdminUser = user?.is_admin || 
                      subscription?.plan_id?.toLowerCase()?.includes('admin') ||
                      (subscription?.plan_id === 'free_trial' && isPremium) ||
                      (subscription?.plan_id === 'free' && isPremium);
  const isActiveUser = isPremium || isAdminUser;

  useEffect(() => {
    // Forcer le rafraîchissement des données à chaque visite de la page
    const refreshPageData = async () => {
      await refreshData();
    };
    
    refreshPageData();
    loadMyReferralCode();
    loadScheduledChange();
    
    // Track premium screen view
    trackEvent(EVENTS.PREMIUM_SCREEN_VIEWED);
  }, [refreshData]);

  const loadScheduledChange = async () => {
    try {
      const response = await api.get('/monetization/status');
      if (response.data?.subscription?.scheduled_plan_change) {
        setScheduledChange(response.data.subscription.scheduled_plan_change);
      }
    } catch (error) {
      console.error('Error loading scheduled change:', error);
    }
  };

  const loadMyReferralCode = async () => {
    try {
      const response = await api.get('/referral/my-code');
      setMyReferralCode(response.data.referral_code);
    } catch (error) {
      console.error('Error loading referral code:', error);
    }
  };

  const getDisplayPlans = () => {
    return SUBSCRIPTION_PLANS.filter(plan => {
      if (plan.id === 'free_trial') return true;
      return isYearly ? plan.duration === 'yearly' : plan.duration === 'monthly';
    }).filter(plan => {
      // Filtrer pour ne garder que Essential et Famille
      return plan.name.includes('Essential') || 
             plan.name.includes('Famille') || 
             plan.name.includes('Family') ||
             plan.id === 'free_trial';
    });
  };

  const handleSelectPlan = async (planId) => {
    const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
    if (!plan || plan.price === 0) return;

    // Si l'utilisateur a déjà un abonnement premium, gérer le changement
    if (isPremium && currentPlan && currentPlan.id !== 'free_trial') {
      await handlePlanChange(planId);
      return;
    }

    // Sinon, créer un nouvel abonnement (première souscription)
    setSelectedPlan(planId);
    setIsUpgrading(true);

    try {
      setIsUpgrading(true);
      setSelectedPlan(plan);
      
      // Utiliser notre endpoint backend pour créer une session Stripe
      const response = await api.post('/monetization/create-checkout-session', {
        planId: plan.id,
        referralCode: referralCode || null
      });
      
      if (response.data.checkout_url) {
        // Rediriger vers la session Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Impossible de créer la session de paiement');
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      alert('Erreur lors de la création de la session de paiement');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handlePlanChange = async (newPlanId) => {
    if (!currentPlan || currentPlan.id === newPlanId) return;

    try {
      setIsUpgrading(true);

      // 1. Valider le changement
      const validationResponse = await api.post('/monetization/validate-plan-change', {
        new_plan_id: newPlanId
      });

      if (!validationResponse.data.allowed) {
        if (validationResponse.data.alternative) {
          alert(`❌ ${validationResponse.data.reason}\n\n💡 Nous vous recommandons plutôt : ${validationResponse.data.alternative}`);
        } else {
          alert(`❌ ${validationResponse.data.reason}`);
        }
        return;
      }

      // 2. Calculer les coûts
      const calculationResponse = await api.post('/monetization/calculate-plan-change', {
        new_plan_id: newPlanId
      });

      const calculation = calculationResponse.data;
      setPlanChangeData(calculation);
      
      // Si c'est un downgrade, afficher l'avertissement de double confirmation
      if (!calculation.is_upgrade) {
        setPendingDowngrade(calculation);
        setShowDowngradeWarning(true);
      } else {
        // Si c'est un upgrade, afficher directement le modal normal
        setShowPlanChangeModal(true);
      }

    } catch (error) {
      console.error('Error handling plan change:', error);
      alert('Erreur lors du calcul du changement de plan');
    } finally {
      setIsUpgrading(false);
    }
  };

  const confirmPlanChange = async () => {
    if (!planChangeData) return;

    try {
      setIsUpgrading(true);

      const response = await api.post('/monetization/apply-plan-change', {
        new_plan_id: planChangeData.new_plan
      });

      // CAS 1: Upgrade avec paiement Stripe requis
      if (response.data.requires_payment && response.data.checkout_url) {
        // Rediriger vers Stripe Checkout
        console.log('Redirection vers Stripe:', response.data.checkout_url);
        window.location.href = response.data.checkout_url;
        return; // Important: ne pas continuer après la redirection
      }
      
      // CAS 2: Changement appliqué directement (downgrade ou upgrade gratuit)
      if (response.data.success) {
        alert(`✅ ${response.data.message || 'Changement de plan appliqué avec succès'}`);
        setShowPlanChangeModal(false);
        setPlanChangeData(null);
        await refreshData();
        await loadScheduledChange();
      } else {
        alert(`❌ ${response.data.error || 'Erreur lors du changement de plan'}`);
      }

    } catch (error) {
      console.error('Error confirming plan change:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de l\'application du changement';
      alert(`❌ ${errorMsg}`);
    } finally {
      setIsUpgrading(false);
    }
  };

  const cancelScheduledChange = async () => {
    try {
      const response = await api.post('/monetization/cancel-scheduled-change');
      
      if (response.data.success) {
        alert(`✅ ${response.data.message}`);
        setScheduledChange(null);
        await refreshData();
      } else {
        alert(`❌ ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error canceling scheduled change:', error);
      alert('Erreur lors de l\'annulation');
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      const result = await cancelSubscription();
      if (result.success) {
        alert('✅ Désabonnement programmé. Votre abonnement sera annulé à la fin de la période de facturation. Vous conservez tous vos avantages jusqu\'à cette date.');
        setShowCancelModal(false);
        await refreshData();
      } else {
        alert(`❌ Erreur: ${result.error || 'Impossible de désabonner'}`);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('❌ Erreur lors du désabonnement');
    }
  };

  const confirmDowngrade = () => {
    if (pendingDowngrade) {
      setPlanChangeData(pendingDowngrade);
      setShowDowngradeWarning(false);
      setShowPlanChangeModal(true);
      setPendingDowngrade(null);
    }
  };

  const handlePurchaseStoryPack = async (priceId, storyCount) => {
    try {
      console.log(`Achat pack: ${storyCount} histoires (${priceId})`);
      
      const response = await api.post('/monetization/purchase-story-pack', {
        price_id: priceId,
        story_count: storyCount
      });
      
      if (response.data.checkout_url) {
        // Rediriger vers la page de paiement Stripe
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Erreur achat pack d\'histoires:', error);
      alert('Erreur lors de l\'achat du pack. Veuillez réessayer.');
    }
  };

  const renderUsageStats = () => {
    // Utiliser les valeurs du contexte de monétisation
    const usedToday = questionsUsedToday || 0;
    const storiesUsed = storiesThisMonth || 0;
    const maxStoriesLimit = currentPlan?.limits?.storiesPerMonth || 1;
    const maxChildren = currentPlan?.limits?.childProfiles || 1;
    const maxQuestions = currentPlan?.limits?.questionsPerDay || 1;
    
    // Déterminer le nom du plan
    let planName = 'Gratuit';
    if (isAdminUser) {
      planName = 'Admin (mensuel)';
    } else if (isPremium && currentPlan?.name) {
      planName = currentPlan.name;
    } else if (currentPlan?.name) {
      planName = currentPlan.name;
    }

    return (
      <div className="mb-6">
        {/* Titre "Mon abonnement" avec icône - Style app mobile */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-2xl">📋</span>
          <h2 
            className="text-xl"
            style={{ fontFamily: '"Caveat", cursive', color: '#666666' }}
          >
            Mon abonnement
          </h2>
        </div>
        
        {/* Card principale - Style app mobile */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          {/* Ligne du haut: Nom du plan + Badge Actif */}
          <div className="flex items-center justify-between mb-4">
            <span 
              className="text-xl font-semibold italic"
              style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
            >
              {planName}
            </span>
            <span 
              className="px-4 py-1 rounded-full text-sm font-bold"
              style={{ 
                background: isActiveUser ? '#10B981' : '#E5E7EB',
                color: isActiveUser ? '#FFFFFF' : '#6B7280'
              }}
            >
              {isActiveUser ? 'Actif' : 'Gratuit'}
            </span>
          </div>
          
          {/* Stats - 3 colonnes avec séparateurs - Style app mobile */}
          <div 
            className="rounded-xl p-4"
            style={{ background: 'rgba(74, 144, 164, 0.08)' }}
          >
            <div className="grid grid-cols-3 gap-2">
              {/* Questions restantes */}
              <div className="text-center relative">
                <div 
                  className="text-2xl font-bold mb-1"
                  style={{ color: '#4A90A4' }}
                >
                  {isActiveUser ? '∞' : Math.max(0, maxQuestions - usedToday)}
                </div>
                <div className="text-xs leading-tight" style={{ color: '#666666' }}>
                  questions<br/>restantes
                </div>
                {/* Séparateur vertical */}
                <div 
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-12 w-px"
                  style={{ background: 'rgba(74, 144, 164, 0.3)' }}
                />
              </div>
              
              {/* Histoires restantes */}
              <div className="text-center relative">
                <div 
                  className="text-2xl font-bold mb-1"
                  style={{ color: '#4A90A4' }}
                >
                  {isActiveUser ? '∞' : Math.max(0, maxStoriesLimit - storiesUsed + (usage?.bonusStories || 0))}
                </div>
                <div className="text-xs leading-tight" style={{ color: '#666666' }}>
                  histoires<br/>restantes
                </div>
                {/* Séparateur vertical */}
                <div 
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-12 w-px"
                  style={{ background: 'rgba(74, 144, 164, 0.3)' }}
                />
              </div>
            
              {/* Enfants max */}
              <div className="text-center">
                <div 
                  className="text-2xl font-bold mb-1"
                  style={{ color: '#4A90A4' }}
                >
                  {isActiveUser ? '∞' : maxChildren}
                </div>
                <div className="text-xs leading-tight" style={{ color: '#666666' }}>
                  enfants<br/>max
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Renewal info for premium - sous la card */}
        {isPremium && subscription?.plan_id !== 'free' && subscription?.plan_id !== 'free_trial' && (subscription?.nextBillingDate || subscription?.next_billing_date) && !subscription?.cancelAtPeriodEnd && !subscription?.cancel_at_period_end && (
          <div 
            className="mt-3 p-3 rounded-xl text-center"
            style={{ background: 'rgba(74, 144, 164, 0.1)' }}
          >
            <span className="text-sm" style={{ color: '#4A90A4' }}>
              🔄 Renouvellement le {new Date(subscription.nextBillingDate || subscription.next_billing_date).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}
        
        {(subscription?.cancelAtPeriodEnd || subscription?.cancel_at_period_end) && (subscription?.nextBillingDate || subscription?.next_billing_date) && (
          <div 
            className="mt-3 p-3 rounded-xl text-center"
            style={{ background: 'rgba(239, 68, 68, 0.1)' }}
          >
            <span className="text-sm" style={{ color: '#EF4444' }}>
              ⚠️ Votre abonnement se termine le {new Date(subscription.nextBillingDate || subscription.next_billing_date).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderPlanCard = (planType) => {
    // planType: 'essential' ou 'family' 
    // Utiliser les états définis au niveau supérieur du composant
    const localIsYearly = planType === 'essential' ? essentialIsYearly : familyIsYearly;
    const setLocalIsYearly = planType === 'essential' ? setEssentialIsYearly : setFamilyIsYearly;
    
    // Obtenir les plans correspondants (mensuel et annuel)
    const monthlyPlan = SUBSCRIPTION_PLANS.find(p => {
      if (planType === 'essential') {
        return p.id === 'essential_monthly';
      } else if (planType === 'family') {
        return p.id === 'family_monthly';
      }
      return false;
    });
    const yearlyPlan = SUBSCRIPTION_PLANS.find(p => {
      if (planType === 'essential') {
        return p.id === 'essential_yearly';
      } else if (planType === 'family') {
        return p.id === 'family_yearly';
      }
      return false;
    });
    
    // Sélectionner le plan à afficher selon le toggle local
    const plan = localIsYearly ? yearlyPlan : monthlyPlan;
    
    if (!plan) return null;
    
    const isCurrentPlan = currentPlan?.id === plan.id;
    const isPopular = planType === 'family';
    
    return (
      <div
        key={planType}
        className="relative overflow-hidden rounded-2xl bg-white transition-all duration-300 hover:shadow-xl"
        style={{ 
          border: isPopular ? '2px solid #4A90A4' : '2px solid transparent',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Badge Populaire - Style app mobile */}
        {isPopular && (
          <div 
            className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold"
            style={{ background: '#4A90A4', color: '#FFFFFF' }}
          >
            ⭐ POPULAIRE
          </div>
        )}

        {/* Badge 2 mois offerts - Style app mobile */}
        {localIsYearly && yearlyPlan?.monthsOffered && (
          <div 
            className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold"
            style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#B45309' }}
          >
            🎁 {yearlyPlan.monthsOffered} mois offerts
          </div>
        )}

        <div className="p-6 pt-12">
          {/* Plan name */}
          <div className="text-center mb-4">
            <h3 
              className="text-2xl font-bold"
              style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
            >
              {plan.name.replace(' (Mensuel)', '').replace(' (Annuel)', '')}
            </h3>
          </div>
          
          {/* Toggle Mensuel/Annuel - Style app mobile */}
          <div className="flex items-center justify-center mb-4">
            <div 
              className="flex rounded-full p-1"
              style={{ background: '#F3F4F6' }}
            >
              <button
                onClick={() => setLocalIsYearly(false)}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={!localIsYearly ? { 
                  background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)',
                  color: '#FFFFFF'
                } : { color: '#666666' }}
              >
                Mensuel
              </button>
              <button
                onClick={() => setLocalIsYearly(true)}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={localIsYearly ? { 
                  background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)',
                  color: '#FFFFFF'
                } : { color: '#666666' }}
              >
                Annuel
              </button>
            </div>
          </div>

          {/* Prix */}
          <div className="text-center mb-6">
            <div className="flex items-end justify-center">
              <span className="text-4xl font-bold text-gray-900">
                {plan.price.toFixed(2)}€
              </span>
              <span className="text-gray-600 ml-2 mb-1">
                /{localIsYearly ? 'an' : 'mois'}
              </span>
            </div>
            {localIsYearly && (
              <div className="text-sm text-green-600 font-medium mt-1">
                Soit {(plan.price / 12).toFixed(2)}€/mois
              </div>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {plan.features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <span className="text-green-500 text-sm mt-0.5">✅</span>
                <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>

          {/* Button */}
          {!isCurrentPlan ? (
            <button
              onClick={() => handleSelectPlan(plan.id)}
              disabled={isUpgrading}
              className="w-full py-3 rounded-full font-bold text-lg text-white transition-all duration-200"
              style={{ 
                background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)',
                opacity: isUpgrading ? 0.7 : 1
              }}
            >
              {isUpgrading ? '⏳' : ''} Choisir {plan.name.replace(' (Mensuel)', '').replace(' (Annuel)', '')}
            </button>
          ) : (
            <div className="text-center py-3">
              <span 
                className="inline-block px-4 py-2 rounded-full font-medium"
                style={{ background: 'rgba(74, 144, 164, 0.1)', color: '#4A90A4' }}
              >
                ✨ Votre plan actuel
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        {/* Header - Style app mobile */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-20">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => window.history.back()}
                className="text-2xl"
                style={{ color: '#333333' }}
              >
                ←
              </button>
              <h1 
                className="text-2xl font-bold"
                style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
              >
                Abonnements
              </h1>
              <div className="w-8"></div> {/* Spacer for centering */}
            </div>
          </div>
        </div>

      <div className="container mx-auto px-4 py-6">
        {renderUsageStats()}

        {/* Star conversion */}
        <StarConversionSection />

        <div className="mb-8">
          {/* Free Plan Banner - visible seulement pour les utilisateurs gratuits */}
          {!isPremium && (
            <div 
              className="p-4 rounded-2xl mb-6"
              style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🌟</span>
                <div>
                  <h3 className="font-bold" style={{ color: '#1E40AF' }}>
                    Ne manquez plus aucun « pourquoi »
                  </h3>
                  <p className="text-sm" style={{ color: '#3B82F6' }}>
                    Chaque question est une occasion d'apprendre ensemble. Offrez-leur des réponses sans limite.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <h2 
            className="text-2xl font-bold text-center mb-6"
            style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
          >
            🎯 Choisissez votre plan
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderPlanCard('essential')}
            {renderPlanCard('family')}
          </div>
        </div>

        {/* Packs d'histoires supplémentaires (seulement pour les utilisateurs premium) */}
        {isPremium && (
          <div className="mb-8">
            <h2 
              className="text-2xl font-bold text-center mb-6"
              style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
            >
              📚 Packs d'histoires
            </h2>
            
            <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
              {/* Pack 5 histoires */}
              <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
                <div className="text-3xl mb-2">📖</div>
                <h3 className="font-bold text-gray-900 mb-1">5 histoires</h3>
                <div 
                  className="text-2xl font-bold mb-3"
                  style={{ color: '#4A90A4' }}
                >
                  4,99€
                </div>
                <button
                  onClick={() => handlePurchaseStoryPack('price_1SFIzjQPJb5o0TOCgMJuiXSm', 5)}
                  className="w-full py-2 rounded-full text-white font-medium"
                  style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                >
                  Acheter
                </button>
              </div>

              {/* Pack 12 histoires - Best Value */}
              <div 
                className="relative bg-white rounded-2xl p-4 shadow-lg text-center"
                style={{ border: '2px solid #F59E0B', background: '#FFFBEB' }}
              >
                {/* Badge Best Value */}
                <div 
                  className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold"
                  style={{ background: '#F59E0B', color: '#FFFFFF' }}
                >
                  MEILLEURE OFFRE
                </div>
                <div className="text-3xl mb-2 mt-2">📚</div>
                <h3 className="font-bold text-gray-900 mb-1">12 histoires</h3>
                <p className="text-xs text-green-600 font-semibold mb-1">10+2 offertes!</p>
                <div 
                  className="text-2xl font-bold mb-3"
                  style={{ color: '#4A90A4' }}
                >
                  9,99€
                </div>
                <button
                  onClick={() => handlePurchaseStoryPack('price_1SFJ1YQPJb5o0TOCkbHqRisI', 12)}
                  className="w-full py-2 rounded-full text-white font-medium"
                  style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                >
                  Acheter
                </button>
              </div>
            </div>
          </div>
        )}

        <DisCard className="p-6 bg-gradient-to-r from-indigo-50 to-blue-100 border border-indigo-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">🛡️ Nos garanties</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🔒</span>
              <span className="text-gray-700">Paiement 100% sécurisé par Stripe</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-500">🔄</span>
              <span className="text-gray-700">Résiliation possible à tout moment</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-teal-500">📞</span>
              <span className="text-gray-700">Support client réactif</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-cyan-500">📱</span>
              <span className="text-gray-700">Accès sur tous vos appareils</span>
            </div>
          </div>
        </DisCard>
        
        {/* Section Mon compte - Style app mobile */}
        <div className="mt-8">
          <h2 
            className="text-xl font-bold mb-4"
            style={{ fontFamily: '"Caveat", cursive', color: '#333333' }}
          >
            👤 Mon compte
          </h2>
          
          {/* Account Card avec gradient violet */}
          <div 
            className="rounded-2xl p-6 mb-6"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                style={{ background: 'rgba(255, 255, 255, 0.25)' }}
              >
                {user?.first_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1">
                <div className="text-white font-bold text-lg">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="text-white/80 text-sm">
                  {user?.email}
                </div>
              </div>
            </div>
            
            {/* Plan actuel et statut */}
            <div className="mt-4 flex justify-between items-center">
              <div>
                <div className="text-white/60 text-xs uppercase tracking-wide">Plan actuel</div>
                <div className="text-white font-bold">
                  {isAdminUser ? 'Admin (mensuel)' : (currentPlan?.name || 'Gratuit')}
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/60 text-xs uppercase tracking-wide">Statut</div>
                <span 
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                  style={{ 
                    background: isActiveUser ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.2)', 
                    color: '#FFFFFF' 
                  }}
                >
                  {isActiveUser ? '✨ Premium' : 'Essai'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Lien Gérer mon abonnement - Stripe Customer Portal (premium seulement) */}
          {isPremium && (
            <button
              onClick={async () => {
                try {
                  const response = await api.post('/monetization/create-portal-session', {
                    return_url: window.location.href
                  });
                  if (response.data.portal_url) {
                    window.location.href = response.data.portal_url;
                  }
                } catch (error) {
                  console.error('Error opening portal:', error);
                  alert('Impossible d\'ouvrir le portail de gestion. Veuillez réessayer.');
                }
              }}
              className="w-full text-center mb-4 underline font-medium"
              style={{ color: '#4A90A4' }}
            >
              Gérer mon abonnement
            </button>
          )}
          
          {/* Bouton Déconnexion - Style app mobile */}
          <button
            onClick={logout}
            className="w-full py-3 rounded-xl font-medium mb-4 text-center"
            style={{ color: '#E57373', background: 'transparent' }}
          >
            Se déconnecter
          </button>
          
          {/* Bouton Supprimer compte - Style app mobile */}
          <button
            onClick={handleCancelSubscription}
            className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2"
            style={{ background: '#FEE2E2', border: '1px solid #FECACA', color: '#DC2626' }}
          >
            🗑️ Supprimer mon compte
          </button>
          
          {/* Textes info - Style app mobile */}
          <div className="mt-6 space-y-3 text-center">
            <p className="text-xs text-gray-500">
              Si vous avez souscrit à votre abonnement via l'application sur l'App Store Apple, merci de gérer votre abonnement via les paramètres de votre compte Apple.
            </p>
            <p className="text-xs text-gray-500">
              🤖 Les réponses et histoires sont générées par intelligence artificielle et adaptées à l'âge de votre enfant. Bien que filtrées et sécurisées, nous vous recommandons d'exercer votre jugement parental avant de les partager.
            </p>
          </div>
          
          {/* Liens légaux */}
          <div className="mt-4 flex justify-center gap-4">
            <a href="/confidentialite" className="text-xs underline" style={{ color: '#4A90A4' }}>
              Politique de confidentialité
            </a>
            <a href="/conditions-utilisation" className="text-xs underline" style={{ color: '#4A90A4' }}>
              Conditions d'utilisation
            </a>
          </div>
        </div>
      </div>

      {/* Modal de paiement */}
      <DisModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="💳 Finaliser l'abonnement"
        size="md"
      >
        {paymentData && (
          <StripePaymentForm
            clientSecret={paymentData.clientSecret}
            planId={paymentData.plan.id}
            onSuccess={async () => {
              setShowPaymentModal(false);
              await refreshData();
              alert('🎉 Abonnement créé avec succès !');
            }}
            onCancel={() => {
              setShowPaymentModal(false);
              setPaymentData(null);
            }}
          />
        )}
      </DisModal>

      {/* Modal de changement de plan */}
      <DisModal
        isOpen={showPlanChangeModal}
        onClose={() => setShowPlanChangeModal(false)}
        title="🔄 Changer d'abonnement"
        size="lg"
      >
        {planChangeData && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-100 p-4 rounded-lg border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-2">
                {planChangeData.is_upgrade ? '⬆️ Upgrade' : '⬇️ Downgrade'}
              </h3>
              <div className="text-sm text-blue-700">
                <strong>Plan actuel :</strong> {SUBSCRIPTION_PLANS.find(p => p.id === planChangeData.current_plan)?.name}
                <br />
                <strong>Nouveau plan :</strong> {SUBSCRIPTION_PLANS.find(p => p.id === planChangeData.new_plan)?.name}
              </div>
            </div>

            {planChangeData.is_upgrade ? (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800 mb-2">✅ Upgrade immédiat</h4>
                <div className="text-sm text-green-700 space-y-1">
                  {planChangeData.immediate_charge > 0 && (
                    <div><strong>Montant à payer :</strong> {planChangeData.immediate_charge.toFixed(2)}€</div>
                  )}
                  {planChangeData.credit_applied > 0 && (
                    <div><strong>Crédit appliqué :</strong> -{planChangeData.credit_applied.toFixed(2)}€</div>
                  )}
                  <div><strong>Jours restants :</strong> {planChangeData.days_remaining}</div>
                  <div className="font-medium text-green-800 mt-2">
                    🚀 Vos nouveaux droits seront actifs immédiatement !
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800 mb-2">📅 Downgrade programmé</h4>
                <div className="text-sm text-orange-700 space-y-1">
                  <div><strong>Effectif le :</strong> {new Date(planChangeData.next_billing_date).toLocaleDateString()}</div>
                  <div><strong>Jours restants avec plan actuel :</strong> {planChangeData.days_remaining}</div>
                  <div className="font-medium text-orange-800 mt-2">
                    🛡️ Vous conservez tous vos avantages actuels jusqu'à la date de changement.
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700">
                {planChangeData.message}
              </p>
            </div>

            <div className="flex space-x-4">
              <DisButton
                onClick={confirmPlanChange}
                loading={isUpgrading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-teal-600 hover:from-blue-600 hover:to-teal-700"
              >
                ✅ Confirmer le changement
              </DisButton>
              <DisButton
                variant="secondary"
                onClick={() => setShowPlanChangeModal(false)}
                className="flex-1"
              >
                ❌ Annuler
              </DisButton>
            </div>
          </div>
        )}
      </DisModal>

      {/* Modal de double confirmation pour désabonnement */}
      <DisModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="🚫 Confirmer le désabonnement"
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-2">⚠️</span>
              <h3 className="font-bold text-red-800">Attention : Désabonnement</h3>
            </div>
            <div className="text-sm text-red-700 space-y-2">
              <p>Vous êtes sur le point de vous désabonner de votre plan <strong>{currentPlan?.name}</strong>.</p>
              <p><strong>Conséquences :</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Votre abonnement sera annulé à la <strong>prochaine date de renouvellement</strong></li>
                <li>Vous conservez tous vos avantages actuels jusqu'à cette date</li>
                <li>Après cette date, vous reviendrez au plan gratuit (limité)</li>
                <li>Vos conversations et données seront conservées</li>
              </ul>
            </div>
          </div>

          {/* Message de downgrade uniquement pour les plans Family */}
          {currentPlan?.id?.includes('family') && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center mb-2">
                <span className="text-xl mr-2">💡</span>
                <h4 className="font-bold text-blue-800">Avant de partir...</h4>
              </div>
              <p className="text-sm text-blue-700">
                Avez-vous pensé à changer pour un plan moins cher plutôt que de vous désabonner ? 
                Le plan Essential pourrait mieux correspondre à vos besoins.
              </p>
            </div>
          )}

          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-600 text-center">
              Cette action est <strong>définitive</strong> et prendra effet à votre prochaine date de facturation.
            </p>
          </div>

          <div className="flex space-x-4">
            <DisButton
              variant="secondary"
              onClick={() => setShowCancelModal(false)}
              className="flex-1"
            >
              🔙 Annuler
            </DisButton>
            <DisButton
              onClick={confirmCancelSubscription}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              ✅ Confirmer le désabonnement
            </DisButton>
          </div>
        </div>
      </DisModal>

      {/* Modal d'avertissement pour downgrade */}
      <DisModal
        isOpen={showDowngradeWarning}
        onClose={() => setShowDowngradeWarning(false)}
        title="⬇️ Attention : Downgrade"
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="flex items-center mb-3">
              <span className="text-2xl mr-2">⚠️</span>
              <h3 className="font-bold text-orange-800">Vous allez perdre des avantages</h3>
            </div>
            <div className="text-sm text-orange-700 space-y-2">
              <p>Vous êtes sur le point de passer de <strong>{SUBSCRIPTION_PLANS.find(p => p.id === pendingDowngrade?.current_plan)?.name}</strong> vers <strong>{SUBSCRIPTION_PLANS.find(p => p.id === pendingDowngrade?.new_plan)?.name}</strong>.</p>
              <p><strong>Que va-t-il se passer :</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Le changement sera effectif à votre <strong>prochaine date de renouvellement</strong></li>
                <li>Vous gardez tous les avantages actuels jusqu'à cette date</li>
                <li>Après cette date, vous aurez les limitations du nouveau plan</li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center mb-2">
              <span className="text-xl mr-2">🤔</span>
              <h4 className="font-bold text-blue-800">Êtes-vous vraiment sûr ?</h4>
            </div>
            <p className="text-sm text-blue-700">
              Cette décision peut être annulée à tout moment avant la date effective. 
              Vous pouvez toujours changer d'avis !
            </p>
          </div>

          <div className="flex space-x-4">
            <DisButton
              variant="secondary"
              onClick={() => setShowDowngradeWarning(false)}
              className="flex-1"
            >
              🔙 Rester sur mon plan actuel
            </DisButton>
            <DisButton
              onClick={confirmDowngrade}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              ⬇️ Confirmer le downgrade
            </DisButton>
          </div>
        </div>
      </DisModal>
      
      {/* Menu flottant */}
      <FloatingMenu />
      
      {/* Footer avec déconnexion */}
      <Footer onLogout={logout} />
    </div>
    </>
  );
};

export default SubscriptionPage;